import React from 'react';
import { ArrowLeft } from 'lucide-react';
import dkvLogo from '../../../../assets/dkv-logo.png';
import idsLogo from '../../../../assets/ids-logo.png';
import meinshkLogo from '../../../../assets/meinshk-logo.png';
import omdLogo from '../../../../assets/omd-logo.png';

export type IntegrationState = 'connected' | 'incomplete' | 'error' | 'off';

const STATE_UI: Record<IntegrationState, { label: string; text: string; dot: string }> = {
  connected: { label: 'Verbunden', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  incomplete: { label: 'Einrichtung unvollständig', text: 'text-amber-800', dot: 'bg-amber-500' },
  error: { label: 'Fehler', text: 'text-red-700', dot: 'bg-red-500' },
  off: { label: 'Nicht verbunden', text: 'text-app-text-muted', dot: 'bg-app-text-subtle' },
};

/** Status als Punkt und Text. */
export function IntegrationStateChip({ state }: { state: IntegrationState }) {
  const ui = STATE_UI[state];
  return (
    <span className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs font-medium ${ui.text}`}>
      <span className={`h-2 w-2 rounded-full ${ui.dot}`} aria-hidden />
      {ui.label}
    </span>
  );
}

// DATEV-Logo nur mit den freigegebenen Dateien aus dem Developer Portal, bis dahin Kürzel.
const LOGOS: Record<string, string> = { IDS: idsLogo, OMD: omdLogo, DKV: dkvLogo, MH: meinshkLogo };
const MARK_TEXT: Record<string, string> = {
  DATEV: 'font-extrabold tracking-tight text-[#1a9a8c]',
};

/** Anbieter-Logo, ohne Logodatei ein Kürzel. */
export function IntegrationMark({ mark, size = 'md' }: { mark: string; size?: 'md' | 'lg' }) {
  const box = size === 'lg' ? 'h-14 w-14 rounded-2xl text-[13px]' : 'h-11 w-11 rounded-xl text-xs';
  const logo = LOGOS[mark];
  // Logos fill the square edge to edge and get no frame, so transparent artwork shows no border.
  if (logo) {
    return (
      <div aria-hidden className={`flex shrink-0 items-center justify-center overflow-hidden ${box}`}>
        <img src={logo} alt="" className="h-full w-full object-contain" />
      </div>
    );
  }
  return (
    <div
      aria-hidden
      className={`flex shrink-0 items-center justify-center overflow-hidden bg-app-surface-muted ${MARK_TEXT[mark] ?? 'font-bold tracking-wide text-app-text'} ${box}`}
    >
      {mark}
    </div>
  );
}

/** Rahmen jeder Integrations-Detailseite: Zurück, Kopf mit Status und Hauptaktion, optional Reiter. */
export function IntegrationDetailLayout({
  mark,
  title,
  state,
  description,
  onBack,
  action,
  tabs,
  children,
}: {
  mark: string;
  title: string;
  state?: IntegrationState | null;
  description: React.ReactNode;
  onBack: () => void;
  /** Wichtigste Aktion rechts im Kopf, z. B. „Einrichten“ oder „Export erstellen“. */
  action?: React.ReactNode;
  tabs?: { items: Array<{ id: string; label: string }>; active: string; onChange: (id: string) => void };
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-[960px] pb-24">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex min-h-8 items-center gap-1.5 text-sm text-app-text-muted hover:text-app-text"
      >
        <ArrowLeft className="h-4 w-4" />
        Alle Integrationen
      </button>

      <header className="mt-4 flex flex-wrap items-center gap-4">
        <IntegrationMark mark={mark} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-2xl font-bold tracking-tight text-app-text">{title}</h1>
            {state ? <IntegrationStateChip state={state} /> : null}
          </div>
          <p className="mt-1 text-sm text-app-text-muted">{description}</p>
        </div>
        {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      </header>

      {tabs ? (
        <div role="tablist" aria-label={`${title}-Bereiche`} className="mt-6 flex gap-6 overflow-x-auto border-b border-app-border">
          {tabs.items.map((tab) => {
            const active = tab.id === tabs.active;
            return (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => tabs.onChange(tab.id)}
                className={`-mb-px shrink-0 border-b-2 pb-3 text-sm ${
                  active ? 'border-pipedrive-blue font-semibold text-app-text' : 'border-transparent font-medium text-app-text-muted hover:text-app-text'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className={tabs ? 'mt-2' : 'mt-8'}>{children}</div>
    </div>
  );
}

/** Einstellungsgruppe: links Titel und Erklärung, rechts die Felder in einer Karte. */
export function SettingsGroup({
  title,
  description,
  children,
  bare = false,
}: {
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  /** Ohne Kartenrahmen, z. B. für Tabellen mit eigener Gestaltung. */
  bare?: boolean;
}) {
  return (
    <section className="grid gap-4 py-6 md:grid-cols-[220px_minmax(0,1fr)] md:gap-10">
      <div>
        <h2 className="text-sm font-semibold text-app-text">{title}</h2>
        {description ? <p className="mt-1 text-sm leading-relaxed text-app-text-muted">{description}</p> : null}
      </div>
      {bare ? (
        <div className="min-w-0">{children}</div>
      ) : (
        <div className="min-w-0 divide-y divide-app-border rounded-xl border border-app-border bg-app-surface">{children}</div>
      )}
    </section>
  );
}

/** Eine Zeile in einer {@link SettingsGroup}: Bezeichnung links, Eingabe rechts. */
export function SettingsField({
  label,
  hint,
  htmlFor,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="min-w-0">
        <label htmlFor={htmlFor} className="text-sm font-medium text-app-text">
          {label}
        </label>
        {hint ? <p className="mt-0.5 text-xs leading-relaxed text-app-text-muted">{hint}</p> : null}
      </div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

/** Erscheint unten, sobald etwas geändert wurde. */
export function UnsavedChangesBar({
  open,
  saving,
  error,
  onSave,
  onDiscard,
}: {
  open: boolean;
  saving: boolean;
  error?: string | null;
  onSave: () => void;
  onDiscard: () => void;
}) {
  if (!open) return null;
  return (
    <div className="sticky bottom-4 z-20 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-app-border bg-app-surface px-5 py-3 shadow-[0_12px_32px_rgba(15,23,42,0.14)]">
      <p className={`text-sm ${error ? 'text-red-700' : 'text-app-text'}`}>{error || 'Ungespeicherte Änderungen'}</p>
      <div className="flex items-center gap-2">
        <button type="button" disabled={saving} onClick={onDiscard} className="rounded-lg px-3 py-1.5 text-sm font-medium text-app-text-muted hover:bg-app-hover hover:text-app-text">
          Verwerfen
        </button>
        <button type="button" disabled={saving} onClick={onSave} className="rounded-lg bg-pipedrive-blue px-3.5 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60">
          {saving ? 'Speichert …' : 'Änderungen speichern'}
        </button>
      </div>
    </div>
  );
}
